/**
 * Mask Renderer - RLE mask rendering with caching
 * Synchronous decoding with cache - only decodes once per unique mask
 */

import { decodeMaskSync } from "./maskDecoderSync";

interface MaskData {
    counts: string;
    size: [number, number];
}

interface DecodedMaskCache {
    imageData: ImageData;
    bounds: {
        minX: number;
        minY: number;
        maxX: number;
        maxY: number;
        width: number;
        height: number;
    };
    offsetX: number;
    offsetY: number;
}

// Cache for decoded masks
const maskCache = new Map<string, DecodedMaskCache>();

function getCacheKey(masks: MaskData): string {
    return masks.counts.substring(0, 100) + masks.size.join("x");
}

function getCachedMask(masks: MaskData): DecodedMaskCache | null {
    return maskCache.get(getCacheKey(masks)) || null;
}

/**
 * Render RLE mask on canvas with automatic caching
 */
export function renderMask(
    ctx: CanvasRenderingContext2D,
    masks: MaskData,
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    showOutline: boolean = false
): boolean {
    const maskOriginalWidth = masks.size[1]; // COCO format: size = [height, width]
    const maskOriginalHeight = masks.size[0];

    const cached = getCachedMask(masks);

    if (cached) {
        renderMaskFromCache(
            ctx,
            cached,
            x,
            y,
            width,
            height,
            color,
            maskOriginalWidth,
            maskOriginalHeight
        );
        return true;
    }

    try {
        const decoded = decodeMaskSync(masks, showOutline);
        maskCache.set(getCacheKey(masks), decoded);

        renderMaskFromCache(
            ctx,
            decoded,
            x,
            y,
            width,
            height,
            color,
            maskOriginalWidth,
            maskOriginalHeight
        );
        return true;
    } catch (error) {
        console.error("Mask decoding failed:", error);
        return false;
    }
}

function renderMaskFromCache(
    ctx: CanvasRenderingContext2D,
    decoded: DecodedMaskCache,
    x: number,
    y: number,
    width: number,
    height: number,
    color: string,
    maskOriginalWidth: number,
    maskOriginalHeight: number
): void {
    ctx.save();

    const dpr = window.devicePixelRatio || 1;
    const canvasCSSWidth = ctx.canvas.width / dpr;
    const canvasCSSHeight = ctx.canvas.height / dpr;

    const labelPadding = parseInt(
        (ctx.canvas as HTMLCanvasElement).dataset.labelPadding || "0",
        10
    );
    const imageHeight = canvasCSSHeight - labelPadding;

    const scaleX = canvasCSSWidth / maskOriginalWidth;
    const scaleY = imageHeight / maskOriginalHeight;

    const tempCanvas = document.createElement("canvas");
    tempCanvas.width = decoded.imageData.width;
    tempCanvas.height = decoded.imageData.height;
    const tempCtx = tempCanvas.getContext("2d")!;

    tempCtx.putImageData(decoded.imageData, 0, 0);

    tempCtx.globalCompositeOperation = "source-atop";
    tempCtx.fillStyle = color;
    tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);

    const scaledOffsetX = decoded.offsetX * scaleX;
    const scaledOffsetY = decoded.offsetY * scaleY;
    const scaledWidth = decoded.imageData.width * scaleX;
    const scaledHeight = decoded.imageData.height * scaleY;

    ctx.drawImage(tempCanvas, scaledOffsetX, scaledOffsetY, scaledWidth, scaledHeight);

    ctx.restore();
}

export function clearMaskCache(): void {
    maskCache.clear();
}

export function setOnMaskReadyCallback(callback: (() => void) | null): void {
    // No-op: sync rendering doesn't need callbacks
}
